import React, { useCallback, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * ZoomPan - desktop/tablet mouse-wheel zoom + drag-pan container.
 * Mirrors the mobile useWebWheelZoom behaviour for web parity.
 */
export default function ZoomPan({ children, className, minScale = 0.3, maxScale = 6 }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const drag = useRef(null);
  const hostRef = useRef(null);

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      const rect = hostRef.current.getBoundingClientRect();
      const ox = e.clientX - rect.left;
      const oy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setScale((s) => {
        const ns = Math.min(maxScale, Math.max(minScale, s * factor));
        const k = ns / s;
        setTx((x) => ox - k * (ox - x));
        setTy((y) => oy - k * (oy - y));
        return ns;
      });
    },
    [minScale, maxScale]
  );

  const onDown = (e) => {
    drag.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    setTx(drag.current.tx + (e.clientX - drag.current.x));
    setTy(drag.current.ty + (e.clientY - drag.current.y));
  };
  const onUp = () => {
    drag.current = null;
  };
  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border border-border bg-white ${className || ""}`}>
      <div
        ref={hostRef}
        className="h-full w-full cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
      >
        <div style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: "0 0" }}>
          {children}
        </div>
      </div>
      <div className="absolute bottom-3 right-3 flex flex-col gap-1.5">
        <button className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-popover/90 text-foreground hover:border-primary" onClick={() => setScale((s) => Math.min(maxScale, s * 1.2))} title="Zoom in">
          <ZoomIn size={16} />
        </button>
        <button className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-popover/90 text-foreground hover:border-primary" onClick={() => setScale((s) => Math.max(minScale, s / 1.2))} title="Zoom out">
          <ZoomOut size={16} />
        </button>
        <button className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-popover/90 text-foreground hover:border-primary" onClick={reset} title="Reset">
          <Maximize2 size={16} />
        </button>
      </div>
    </div>
  );
}
